import React, { useState, useCallback, useRef } from "react";
import { Button, Form, Alert, Badge, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import api from "../../../services/httpClient";
import "../../../assets/styles/layers.scss";

/**
 * ImportLayerComponent — komponent importu plików SHP
 * Props:
 *   onLayerPreview(geoJsonArray)  — callback do wyświetlania warstw na mapie
 *   onLayerToggle(index, visible) — callback do ukrywania/pokazywania warstwy na mapie
 *   onLayerFocus(geoJson)         — callback do zoom na warstwę
 */
const ImportLayerComponent = ({ onLayerPreview, onLayerToggle, onLayerFocus }) => {
    // Stan upload
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef(null);

    // Stan parsowania / importu
    const [parsedLayers, setParsedLayers] = useState([]); // {name, area, fileIndex, featureIndex, status:'pending'|'importing'|'imported'|'error', errorMsg}
    const [importedLayers, setImportedLayers] = useState([]); // {layerId, name, area, color, visible}
    const [isLoading, setIsLoading] = useState(false);
    const [globalError, setGlobalError] = useState(null);
    const [globalSuccess, setGlobalSuccess] = useState(null);

    // Obsługa drag & drop
    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(false);
        const files = Array.from(e.dataTransfer.files).filter(f => f.name.toLowerCase().endsWith('.shp'));
        if (files.length === 0) {
            setGlobalError("Nie znaleziono plików .shp. Dozwolone rozszerzenie: .shp");
            return;
        }
        setSelectedFiles(prev => [...prev, ...files]);
        setGlobalError(null);
    }, []);

    // Obsługa input file
    const handleFileSelect = useCallback((e) => {
        const files = Array.from(e.target.files).filter(f => f.name.toLowerCase().endsWith('.shp'));
        if (files.length === 0 && e.target.files.length > 0) {
            setGlobalError("Niedozwolone rozszerzenie pliku. Dozwolone: .shp");
            return;
        }
        setSelectedFiles(prev => [...prev, ...files]);
        setGlobalError(null);
        e.target.value = ''; // reset input
    }, []);

    // Usunięcie pliku z listy
    const removeFile = useCallback((index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    }, []);

    // Parsowanie plików — podgląd
    const handleParse = useCallback(async () => {
        if (selectedFiles.length === 0) return;

        setIsLoading(true);
        setGlobalError(null);
        setGlobalSuccess(null);
        setParsedLayers([]);

        try {
            const formData = new FormData();
            selectedFiles.forEach(file => formData.append('files', file));

            const response = await api.post('Layer/ParseShp', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const results = response.data.results || [];
            const layers = [];
            const errors = [];

            results.forEach((result, fileIndex) => {
                if (result.errors && result.errors.length > 0) {
                    errors.push(`${result.fileName}: ${result.errors.join(', ')}`);
                }
                if (result.layers) {
                    result.layers.forEach(layer => {
                        layers.push({
                            name: layer.name,
                            area: layer.area,
                            fileIndex,
                            featureIndex: layer.featureIndex,
                            fileName: result.fileName,
                            status: 'pending',
                            errorMsg: null
                        });
                    });
                }
            });

            setParsedLayers(layers);

            if (errors.length > 0) {
                setGlobalError(errors.join('\n'));
            }

            // Wyślij podgląd warstw na mapę
            if (onLayerPreview && layers.length > 0) {
                onLayerPreview(layers.map(l => l.area));
            }

        } catch (err) {
            const msg = err.response?.data?.message || err.message || "Błąd parsowania plików.";
            setGlobalError(msg);
        } finally {
            setIsLoading(false);
        }
    }, [selectedFiles, onLayerPreview]);

    // Import pojedynczej warstwy (przycisk "Przypisz")
    const handleImportSingle = useCallback(async (layerIndex) => {
        const layer = parsedLayers[layerIndex];
        if (!layer || layer.status !== 'pending') return;

        setParsedLayers(prev => prev.map((l, i) => i === layerIndex ? { ...l, status: 'importing' } : l));

        try {
            const response = await api.post('Layer/ImportSingleShpLayer', {
                name: layer.name,
                area: layer.area,
                color: '#f09800'
            });

            const layerId = response.data.layerId;

            // Oznacz jako zaimportowaną
            setParsedLayers(prev => prev.map((l, i) => i === layerIndex ? { ...l, status: 'imported' } : l));

            // Dodaj do listy zaimportowanych
            setImportedLayers(prev => [...prev, {
                layerId,
                name: layer.name,
                area: layer.area,
                color: '#f09800',
                visible: true
            }]);

            setGlobalSuccess(`Warstwa "${layer.name}" została zaimportowana (ID: ${layerId}).`);
        } catch (err) {
            const msg = err.response?.data?.message || "Błąd importu warstwy.";
            setParsedLayers(prev => prev.map((l, i) => i === layerIndex ? { ...l, status: 'error', errorMsg: msg } : l));
        }
    }, [parsedLayers]);

    // Import wszystkich warstw (przycisk "Importuj wszystkie")
    const handleImportAll = useCallback(async () => {
        if (selectedFiles.length === 0) return;

        setIsLoading(true);
        setGlobalError(null);
        setGlobalSuccess(null);

        try {
            const formData = new FormData();
            selectedFiles.forEach(file => formData.append('files', file));

            const response = await api.post('Layer/ImportShp', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const data = response.data;
            const results = data.results || [];
            const errors = [];
            let totalImported = 0;

            results.forEach(result => {
                if (result.errors && result.errors.length > 0) {
                    errors.push(`${result.fileName}: ${result.errors.join(', ')}`);
                }
                if (result.importedLayers) {
                    result.importedLayers.forEach(il => {
                        setImportedLayers(prev => [...prev, {
                            layerId: il.layerId,
                            name: il.name,
                            area: il.area,
                            color: il.color || '#f09800',
                            visible: true
                        }]);
                    });
                    totalImported += result.importedCount || 0;
                }
            });

            // Oznacz wszystkie sparsowane jako zaimportowane
            setParsedLayers(prev => prev.map(l => ({ ...l, status: 'imported' })));
            setSelectedFiles([]);

            if (errors.length > 0) {
                setGlobalError(errors.join('\n'));
            }

            if (totalImported > 0) {
                setGlobalSuccess(`Zaimportowano ${totalImported} warstw(y).`);
            }

        } catch (err) {
            const msg = err.response?.data?.message || err.message || "Błąd importu plików.";
            setGlobalError(msg);
        } finally {
            setIsLoading(false);
        }
    }, [selectedFiles]);

    // Przełączanie widoczności zaimportowanej warstwy
    const toggleImportedVisibility = useCallback((index) => {
        setImportedLayers(prev => {
            const updated = prev.map((l, i) => i === index ? { ...l, visible: !l.visible } : l);
            if (onLayerToggle) {
                onLayerToggle(index, updated[index].visible);
            }
            return updated;
        });
    }, [onLayerToggle]);

    // Zoom na warstwę
    const focusLayer = useCallback((layer) => {
        if (onLayerFocus) {
            onLayerFocus(layer.area);
        }
    }, [onLayerFocus]);

    const pendingCount = parsedLayers.filter(l => l.status === 'pending').length;

    return (
        <div className="shp-import-panel">


            {/* Instrukcja dla użytkownika */}
            {/*parsedLayers.length === 0 && importedLayers.length === 0 && selectedFiles.length === 0 &&*/ (
                <div className="shp-instructions">
                    <h6>ℹ️ Jak korzystać z importu SHP?</h6>
                    <ol>
                        <li><strong>Wybierz pliki</strong> — przeciągnij pliki .shp do strefy powyżej lub kliknij, aby wybrać z dysku.</li>
                        <li><strong>Podgląd</strong> — kliknij "Podgląd warstw", aby zobaczyć kształty na mapie przed importem.</li>
                        <li><strong>Przypisz</strong> — zapisz pojedynczą warstwę do bazy danych.</li>
                        <li><strong>Importuj wszystkie</strong> — zapisz wszystkie warstwy jednocześnie.</li>
                        <li><strong>Zarządzaj</strong> — po imporcie możesz ukrywać/pokazywać warstwy ikoną 👁️ oraz przybliżać na mapie ikoną 📍.</li>
                    </ol>
                </div>
            )}
            {/* Sekcja 1: Upload plików */}
            <div className="shp-section">
                <div className="shp-section-header">
                    <div>
                        <h5 className="shp-section-title">
                            📂 Wybierz pliki .shp
                        </h5>
                        <p className="shp-section-subtitle">
                            Dodaj pliki SHP, wykonaj podgląd, a następnie zdecyduj, które warstwy zaimportować.
                        </p>
                    </div>

                    {selectedFiles.length > 0 && (
                        <Badge bg="secondary">
                            {selectedFiles.length} plików
                        </Badge>
                    )}
                </div>

                <div
                    className={`shp-dropzone ${isDragOver ? 'shp-dropzone-active' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <div className="shp-dropzone-content">
                        <span className="shp-dropzone-icon">📁</span>
                        <span className="shp-dropzone-text">
                            Przeciągnij pliki tutaj lub <strong>kliknij, aby wybrać z dysku</strong>
                        </span>
                        <span className="shp-dropzone-hint">
                            Dozwolone rozszerzenie: <strong>.shp</strong>
                        </span>
                    </div>

                    <Form.Control
                        ref={fileInputRef}
                        type="file"
                        accept=".shp"
                        multiple
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />
                </div>

                {selectedFiles.length > 0 && (
                    <div className="shp-file-list">
                        <div className="shp-file-list-header">
                            <span>Plik</span>
                            <span>Rozmiar</span>
                            <span></span>
                        </div>

                        {selectedFiles.map((file, i) => (
                            <div key={i} className="shp-file-item">
                                <span className="shp-file-name" title={file.name}>
                                    📄 {file.name}
                                </span>
                                <span className="shp-file-size">
                                    {(file.size / 1024).toFixed(1)} KB
                                </span>
                                <button
                                    className="shp-file-remove"
                                    onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                                    title="Usuń plik"
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {selectedFiles.length > 0 && (
                    <div className="shp-actions">
                        <Button
                            variant="outline-primary"
                            onClick={handleParse}
                            disabled={isLoading}
                            className="shp-btn"
                        >
                            {isLoading ? <Spinner size="sm" className="me-2" /> : null}
                            👁️ Podgląd
                        </Button>

                        <Button
                            variant="danger"
                            onClick={handleImportAll}
                            disabled={isLoading}
                            className="shp-btn shp-btn-import-all"
                        >
                            {isLoading ? <Spinner size="sm" className="me-2" /> : null}
                            📥 Importuj wszystkie
                        </Button>
                    </div>
                )}
            </div>


            {/* Komunikaty */}
            {globalError && (
                <Alert variant="danger" className="shp-alert" dismissible onClose={() => setGlobalError(null)}>
                    <Alert.Heading>⚠️ Błąd</Alert.Heading>
                    <pre className="shp-error-pre">{globalError}</pre>
                </Alert>
            )}

            {globalSuccess && (
                <Alert variant="success" className="shp-alert" dismissible onClose={() => setGlobalSuccess(null)}>
                    ✅ {globalSuccess}
                </Alert>
            )}


            {/* Sekcja 2: Lista warstw do importu */}
            {parsedLayers.length > 0 && (
                <div className="shp-section">
                    <div className="shp-section-header">
                        <div>
                            <h5 className="shp-section-title">
                                📋 Warstwy do importu
                            </h5>
                            <p className="shp-section-subtitle">
                                Sprawdź wykryte warstwy. Możesz przybliżyć warstwę na mapie albo przypisać ją pojedynczo.
                            </p>
                        </div>

                        {pendingCount > 0 && (
                            <Badge bg="warning" text="dark">
                                {pendingCount} oczekujących
                            </Badge>
                        )}
                    </div>

                    <div className="shp-layers-table">
                        <div className="shp-layers-table-header">
                            <span>Warstwa</span>
                            <span>Plik</span>
                            <span>Status</span>
                            <span>Akcje</span>
                        </div>

                        <div className="shp-layers-list">
                            {parsedLayers.map((layer, i) => (
                                <div key={i} className={`shp-layer-item shp-layer-${layer.status}`}>
                                    <div className="shp-layer-main">
                                        <span className="shp-layer-color" style={{ backgroundColor: '#f09800' }} />
                                        <span className="shp-layer-name" title={layer.name}>
                                            {layer.name}
                                        </span>
                                    </div>

                                    <span className="shp-layer-file" title={layer.fileName}>
                                        {layer.fileName}
                                    </span>

                                    <div className="shp-layer-status">
                                        {layer.status === 'pending' && (
                                            <Badge bg="warning" text="dark">
                                                Oczekuje
                                            </Badge>
                                        )}

                                        {layer.status === 'imported' && (
                                            <Badge bg="success">
                                                ✓ OK
                                            </Badge>
                                        )}

                                        {layer.status === 'importing' && (
                                            <Spinner size="sm" />
                                        )}

                                        {layer.status === 'error' && (
                                            <OverlayTrigger overlay={<Tooltip>{layer.errorMsg}</Tooltip>}>
                                                <Badge bg="danger">
                                                    Błąd
                                                </Badge>
                                            </OverlayTrigger>
                                        )}
                                    </div>

                                    <div className="shp-layer-actions">
                                        {layer.status === 'pending' && (
                                            <>
                                                <button
                                                    className="shp-icon-btn"
                                                    onClick={() => onLayerFocus && onLayerFocus(layer.area)}
                                                    title="Pokaż na mapie"
                                                >
                                                    📍
                                                </button>

                                                <Button
                                                    size="sm"
                                                    variant="outline-success"
                                                    onClick={() => handleImportSingle(i)}
                                                    disabled={isLoading}
                                                >
                                                    Przypisz
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}


            {/* Sekcja 3: Lista zaimportowanych warstw */}
            {importedLayers.length > 0 && (
                <div className="shp-section">
                    <div className="shp-section-header">
                        <div>
                            <h5 className="shp-section-title">
                                ✅ Zaimportowane warstwy
                            </h5>
                            <p className="shp-section-subtitle">
                                Zarządzaj widocznością zaimportowanych warstw oraz szybko przybliżaj je na mapie.
                            </p>
                        </div>

                        <Badge bg="success">
                            {importedLayers.length}
                        </Badge>
                    </div>

                    <div className="shp-imported-card-list">
                        {importedLayers.map((layer, i) => (
                            <div key={i} className={`shp-imported-card ${!layer.visible ? 'shp-layer-hidden' : ''}`}>
                                <div className="shp-imported-meta">
                                    <span className="shp-layer-color" style={{ backgroundColor: layer.color }} />
                                    <span className="shp-layer-name" title={layer.name}>
                                        {layer.name}
                                    </span>
                                    <Badge bg="secondary">
                                        ID: {layer.layerId}
                                    </Badge>
                                    {!layer.visible && (
                                        <Badge bg="light" text="dark">
                                            Ukryta
                                        </Badge>
                                    )}
                                </div>

                                <div className="shp-layer-actions">
                                    <button
                                        className="shp-icon-btn"
                                        onClick={() => toggleImportedVisibility(i)}
                                        title={layer.visible ? "Ukryj warstwę" : "Pokaż warstwę"}
                                    >
                                        {layer.visible ? '👁️' : '🙈'}
                                    </button>

                                    <button
                                        className="shp-icon-btn"
                                        onClick={() => focusLayer(layer)}
                                        title="Przybliż na mapie"
                                    >
                                        📍
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImportLayerComponent;